import React, { useEffect, useState } from 'react';
import { GeojsonProperties, Site, SiteFindByInputObject } from 'graphql-common';
import {
  ApolloClient,
  ErrorLike,
  gql,
  TypedDocumentNode,
} from '@apollo/client';
import ApolloClientSingleton from 'graphql-common/ApolloClient';
import Trans from '@lib/components/Trans/Trans';
import Typography from '@lib/components/Typography/Typography';
import SiteMapPopupContentLoading from './SiteMapPopupContentLoading';
import SiteMapPopupContent from './SiteMapPopupContent';
import styles from './SiteMapPopupContent.module.scss';

type Props = {
  siteId: string;
  properties: GeojsonProperties;
  coordinates: number[];
  isOpen?: boolean;
};

const SITE_QUERY: TypedDocumentNode<
  { site: Site },
  { findBy: SiteFindByInputObject }
> = gql`
  query Site($findBy: SiteFindByInputObject!) {
    site(findBy: $findBy) {
      id
      name
      code
      locationPoint {
        kind
        id
        geojson {
          features {
            geometry {
              coordinates
              type
            }
            properties {
              address
              addressDetails
              color
              icon
              shape
              type
              pointCount
              sourceables {
                id
                type
              }
            }
            type
          }
          type
        }
      }
    }
  }
`;

export default function SiteMapPopupContentAsyncWrap(props: Props) {
  const { siteId, properties, coordinates, isOpen } = props;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Site | null>(null);
  const [error, setError] = useState<ErrorLike | null>(null);

  const client: ApolloClient = ApolloClientSingleton;

  useEffect(() => {
    if (isOpen && siteId) {
      setLoading(true);
      setError(null);
      client
        .query({
          query: SITE_QUERY,
          variables: { findBy: { id: siteId } },
        })
        .then((res) => {
          if (res.data?.site) {
            setData(res.data.site as Site);
          }
          if (res.error) {
            setError(res.error);
          }
          setLoading(false);
        });
    }
  }, [siteId, isOpen, client]);

  let content = <SiteMapPopupContentLoading />;
  if (!loading && data) {
    content = (
      <SiteMapPopupContent
        site={data}
        properties={properties}
        coordinates={coordinates}
      />
    );
  }
  if (error) {
    return (
      <Typography variant="body" className={styles.error}>
        <Trans i18nKey="failed-to-load-text" />
      </Typography>
    );
  }
  return content;
}
